import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Task, TaskStatus, TaskPriority, TaskType } from '@/types/employee/task';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit, 
  startAfter,
  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Helper function to filter mock data
function filterMockData<T extends { title: string; status: string; priority: string; type: string; assignedTo: { name: string; id: string } }>(
  data: T[],
  searchTerm?: string,
  status?: string,
  priority?: string,
  assignedTo?: string,

): T[] {
  let filteredData = data;
  
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(item => 
      item.title.toLowerCase().includes(lowercasedSearch) ||
      item.assignedTo.name.toLowerCase().includes(lowercasedSearch)
    );
  }
  
  if (status) {
    filteredData = filteredData.filter(item => item.status === status);
  }
  
  if (priority) {
    filteredData = filteredData.filter(item => item.priority === priority);
  }
  
  if (assignedTo) {
    filteredData = filteredData.filter(item => item.assignedTo.id === assignedTo);
  }
  
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const TASKS_COLLECTION = 'tasks';

// Types
export interface TasksQueryParams {
  searchTerm?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  type?: string; // Changed from TaskType to string to match mock data
  assignedTo?: string;
  assignedBy?: string;
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
  category?: string; // Added for mock data filtering
  blockId?: string; // Added for mock data filtering
  apartmentUnitId?: string; // Added for mock data filtering
  page?: number; // Added for mock data pagination
}

export interface CreateTaskInput extends Omit<Task, 'id' | 'assignedTo' | 'assignedBy'> {
  type: TaskType;
  assignedTo: {
    id: string;
    name: string;
  };
  assignedBy: {
    id: string;
    name: string;
  };
}

export interface UpdateTaskInput extends Partial<CreateTaskInput> {
  id: string;
}

type TaskWithDates = Partial<Task>;

// Task API
export async function getTasks({
  searchTerm,
  category,
  status,
  priority,
  assignedTo,
  assignedBy,

  pageSize,
  page = 1,
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: TasksQueryParams): Promise<{ tasks: Task[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock tasks with filters', { searchTerm, category, status, priority, assignedTo, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockTasks = await loadMockData<Task[]>('tasks', firestoreUser.language);
    const filteredData = filterMockData(mockTasks, searchTerm, status, priority, assignedTo);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      tasks: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }

  console.log('API Call: getTasks', { searchTerm, status, priority, assignedTo, assignedBy, pageSize, lastDoc, sortBy, sortOrder, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const tasksRef = collection(db, TASKS_COLLECTION);
    let q = query(
      tasksRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const tasks = snapshot.docs
      .map(doc => {
        const data = doc.data() as TaskWithDates;
        const mapped: TaskWithDates = {
          ...data,
          id: doc.id,
          dueDate: data.dueDate ? formatFirestoreTimestamp(data.dueDate) ?? undefined : undefined,
          completedAt: data.completedAt ? formatFirestoreTimestamp(data.completedAt) ?? undefined : undefined
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.type === 'string' &&
          typeof mapped.status === 'string' &&
          typeof mapped.priority === 'string' &&
          mapped.assignedTo && typeof mapped.assignedTo.id === 'string' &&
          mapped.assignedBy && typeof mapped.assignedBy.id === 'string' &&
          mapped.dueDate instanceof Date
        ) {
          return mapped as Task;
        }
        return undefined;
      })
      .filter((t): t is Task => !!t);

    return {
      tasks,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: tasks.length
    };
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw new Error('Failed to fetch tasks');
  }
}

export async function getTaskById(id: string, firestoreUser: User): Promise<Task | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock task with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockTasks = await loadMockData<Task[]>('tasks', firestoreUser.language);
    const task = mockTasks.find(t => t.id === id) || null;
    return task;
  }

  console.log('API Call: getTaskById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const tasksRef = collection(db, TASKS_COLLECTION);
    const q = query(
      tasksRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const taskData = snapshot.docs[0].data() as TaskWithDates;
    const mappedTask: TaskWithDates = {
      ...taskData,
      id: snapshot.docs[0].id,
      dueDate: taskData.dueDate ? formatFirestoreTimestamp(taskData.dueDate) ?? undefined : undefined,
      completedAt: taskData.completedAt ? formatFirestoreTimestamp(taskData.completedAt) ?? undefined : undefined
    };
    if (
      typeof mappedTask.title === 'string' &&
      typeof mappedTask.description === 'string' &&
      typeof mappedTask.type === 'string' &&
      typeof mappedTask.status === 'string' &&
      typeof mappedTask.priority === 'string' &&
      mappedTask.assignedTo && typeof mappedTask.assignedTo.id === 'string' &&
      mappedTask.assignedBy && typeof mappedTask.assignedBy.id === 'string' &&
      mappedTask.dueDate instanceof Date
    ) {
      return mappedTask as Task;
    }
    return null;
  } catch (error) {
    console.error('Error fetching task:', error);
    throw new Error('Failed to fetch task');
  }
}

export async function addTask(
  task: CreateTaskInput,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addTask', { task, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const taskData = addWorkspaceAndActivity(
      {
        ...task,
        dueDate: task.dueDate,
        completedAt: task.completedAt,
        apartmentUnit: task.apartmentUnit || undefined,
        asset: (task as any).asset || undefined
      },
      firestoreUser,
      'created'
    );

    const tasksRef = collection(db, TASKS_COLLECTION);
    const newTaskRef = doc(tasksRef);
    
    await setDoc(newTaskRef, removeUndefined({
      ...taskData,
      id: newTaskRef.id
    }));

    // Create activity record
    await createActivityRecord(
      'created',
      taskData,
      firestoreUser
    );

    return newTaskRef.id;
  } catch (error) {
    console.error('Error adding task:', error);
    throw new Error('Failed to add task');
  }
}

export async function updateTask(
  id: string,
  data: Partial<UpdateTaskInput>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateTask', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const tasksRef = collection(db, TASKS_COLLECTION);
    const q = query(
      tasksRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Task not found');
    }

    const taskDoc = snapshot.docs[0];
    const updateData = removeUndefined({
      ...data,
      apartmentUnit: data.apartmentUnit || undefined,
      asset: (data as any).asset || undefined
    });

    await updateDoc(taskDoc.ref, updateData);

    // Create activity record
    await createActivityRecord(
      'updated',
      updateData,
      firestoreUser
    );
  } catch (error) {
    console.error('Error updating task:', error);
    throw new Error('Failed to update task');
  }
}

export async function deleteTask(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteTask', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const tasksRef = collection(db, TASKS_COLLECTION);
    const q = query(
      tasksRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Task not found');
    }

    const taskDoc = snapshot.docs[0];
    await deleteDoc(taskDoc.ref);

    // Create activity record
    await createActivityRecord(
      'deleted',
      taskDoc.data(),
      firestoreUser
    );
  } catch (error) {
    console.error('Error deleting task:', error);
    throw new Error('Failed to delete task');
  }
}

export async function getTasksByStatus(status: TaskStatus, firestoreUser: User): Promise<Task[]> {
  console.log('API Call: getTasksByStatus', { status, firestoreUser });
  
  const { tasks } = await getTasks({ status, firestoreUser, pageSize: 100 });
  return tasks;
}

export async function getTasksByEmployee(employeeId: string, firestoreUser: User): Promise<Task[]> {
  console.log('API Call: getTasksByEmployee', { employeeId, firestoreUser });
  
  const { tasks } = await getTasks({ assignedTo: employeeId, firestoreUser, pageSize: 100 });
  return tasks;
}

export async function getTasksByDueDate(dueDate: Date, firestoreUser: User): Promise<Task[]> {
  console.log('API Call: getTasksByDueDate', { dueDate, firestoreUser });
  
  const { tasks } = await getTasks({ firestoreUser, pageSize: 100 });
  return tasks.filter(task => {
    const taskDate = new Date(task.dueDate);
    return taskDate.toDateString() === dueDate.toDateString();
  });
} 